import React, { useState, useEffect } from 'react';
import { Modal, Input, Typography, Alert } from 'antd';

const { Text } = Typography;

export interface DangerousActionModalProps {
  open: boolean;
  title: string;
  /** 警告說明（會用 danger Alert 顯示） */
  warning: React.ReactNode;
  /** 使用者必須輸入這個字串才能啟用確認鈕（通常是物件名稱） */
  confirmKeyword: string;
  /** 提示文字，告訴使用者要輸入什麼 */
  confirmHint?: React.ReactNode;
  /** 可選額外欄位（例如下架原因 textarea） */
  extra?: React.ReactNode;
  /** 額外必填欄位的驗證結果；false 時確認鈕保持禁用。 */
  isExtraValid?: boolean;
  /** 可選冷卻秒數，倒數結束前 OK 按鈕 disable */
  cooldownSeconds?: number;
  confirming?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
}

/**
 * Type-to-confirm 危險動作彈窗（Stripe / AWS / Cloudflare pattern）。
 * - 必須輸入 confirmKeyword 完全相符才 enable 確認鈕
 * - 可選冷卻倒數（OTA 發布 / 全站廣播類）
 * - 關閉時自動 reset 輸入
 */
export const DangerousActionModal: React.FC<DangerousActionModalProps> = ({
  open,
  title,
  warning,
  confirmKeyword,
  confirmHint,
  extra,
  isExtraValid = true,
  cooldownSeconds = 0,
  confirming = false,
  onConfirm,
  onCancel,
  confirmLabel = '確認執行',
}) => {
  const [typed, setTyped] = useState('');
  const [remaining, setRemaining] = useState(cooldownSeconds);

  // 開啟時 reset
  useEffect(() => {
    if (open) {
      setTyped('');
      setRemaining(cooldownSeconds);
    }
  }, [open, cooldownSeconds]);

  // 冷卻倒數
  useEffect(() => {
    if (!open || remaining <= 0) return;
    const timer = setTimeout(() => setRemaining((n) => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [open, remaining]);

  const keywordOk = typed === confirmKeyword;
  const canConfirm =
    keywordOk && isExtraValid && remaining === 0 && !confirming;

  return (
    <Modal
      title={title}
      open={open}
      onOk={onConfirm}
      onCancel={onCancel}
      confirmLoading={confirming}
      okText={remaining > 0 ? `${confirmLabel}（${remaining}s）` : confirmLabel}
      okButtonProps={{ danger: true, disabled: !canConfirm }}
      cancelText="取消"
      maskClosable={false}
      destroyOnHidden
    >
      <Alert type="warning" showIcon message={warning} style={{ marginBottom: 16 }} />
      <div style={{ marginBottom: 8 }}>
        {confirmHint ?? (
          <span>
            為防止誤操作，請輸入 <Text code>{confirmKeyword}</Text> 確認：
          </span>
        )}
      </div>
      <Input
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder={confirmKeyword}
        autoFocus
        aria-label="輸入確認關鍵字"
        status={typed && !keywordOk ? 'error' : undefined}
      />
      {extra && <div style={{ marginTop: 16 }}>{extra}</div>}
    </Modal>
  );
};
